import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { Add, Delete, ViewList, ViewModule, Visibility } from "@material-ui/icons";
import { css } from "@emotion/css";
import { Link, useHistory } from "react-router-dom";
import { useDebounce } from "utils/hooks";
import { oneOf, useViewState } from "utils/useViewState";
import { CTable } from "shared";
import CCell from "shared/CTable/CCell";
import { isUsMarket } from "utils/market";
import { CatalogueKind, CatalogueRollout, ENV_GROUPS, isEnvGroup } from "routes/createContent/api/catalogueDeployments";
import { groupLabel } from "routes/healthForms/rolloutText";
import { CatalogueDeploymentRow } from "./CatalogueDeploymentRow";
import { LibraryItem } from "./LibraryItem/LibraryItem";
import { CardContainer } from "./styles";
import { ALL_LOCALES, LOCALES, TARGET_JOINTS } from "../views/lessons/constants";

const DEFAULT_LOCALE = isUsMarket ? "en-us" : "sv-se";
const menuProps = { disableScrollLock: true };
const ROWS_PER_PAGE = [10, 25, 50, 100, 200, 500];
const DEFAULT_LIMIT = 25;

const VIEWS = ["table", "cards"] as const;

export const useCatalogueListControls = ({
  inUrl,
  filterKeys = [],
}: {
  inUrl: boolean;
  filterKeys?: readonly string[];
}) => {
  const [state, set] = useViewState(
    {
      search: "",
      locale: DEFAULT_LOCALE,
      page: 0,
      limit: DEFAULT_LIMIT,
      view: "table",
      deleted: false,
      outdated: [] as string[],
      ...Object.fromEntries(filterKeys.map(key => [key, [] as string[]])),
    },
    { inUrl }
  );
  const [search, setSearch] = useState(state.search);
  const debounced = useDebounce(search);
  const filter = (changes: Partial<typeof state>) => set({ ...changes, page: 0 });

  useEffect(() => {
    if (debounced !== state.search) filter({ search: debounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return {
    search,
    setSearch,
    debouncedSearch: state.search,
    locale: oneOf(ALL_LOCALES, state.locale, DEFAULT_LOCALE),
    setLocale: (locale: string) => filter({ locale }),
    page: Math.max(state.page, 0),
    setPage: (page: number) => set({ page }),
    limit: oneOf(ROWS_PER_PAGE, state.limit, DEFAULT_LIMIT),
    setLimit: (limit: number) => filter({ limit }),
    view: oneOf<string>(VIEWS, state.view, "table"),
    setView: (view: (typeof VIEWS)[number]) => set({ view }),
    showDeleted: state.deleted,
    setShowDeleted: (deleted: boolean) => filter({ deleted }),
    outdated: state.outdated.filter(isEnvGroup),
    setOutdated: (outdated: string[]) => filter({ outdated }),
    multi: (key: string) => (state as Record<string, unknown>)[key] as string[],
    setMulti: (key: string) => (values: string[]) => filter({ [key]: values }),
  };
};

export type CatalogueListControls = ReturnType<typeof useCatalogueListControls>;

const CatalogueListHeader: React.VFC<{ title: string; newLabel: string; newPath: string }> = ({
  title,
  newLabel,
  newPath,
}) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
    <h2 style={{ margin: 0 }}>{title}</h2>
    <Button color="primary" variant="contained" size="small" startIcon={<Add />} component={Link} to={newPath}>
      {newLabel}
    </Button>
  </Box>
);

const CatalogueLocaleSelect: React.VFC<{ value: string; onChange: (locale: string) => void }> = ({
  value,
  onChange,
}) => (
  <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
    <InputLabel>Locale</InputLabel>
    <Select label="Locale" value={value} onChange={e => onChange(e.target.value as string)} MenuProps={menuProps}>
      {LOCALES.map(l => (
        <MenuItem key={l.code} value={l.code}>
          {l.flag} {l.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const MultiSelectFilter: React.VFC<{
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (value: string[]) => void;
  optionLabel?: (option: string) => string;
}> = ({ label, options, value, onChange, optionLabel = humanize }) => (
  <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      label={label}
      value={value}
      onChange={e => onChange(e.target.value as string[])}
      renderValue={selected => (selected as string[]).map(optionLabel).join(", ")}
      MenuProps={menuProps}
    >
      {options.map(option => (
        <MenuItem key={option} value={option}>
          <Checkbox size="small" checked={value.includes(option)} />
          {optionLabel(option)}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const ViewToggle: React.VFC<{ controls: CatalogueListControls }> = ({ controls }) => (
  <Box display="flex">
    <Tooltip title="Table">
      <IconButton
        size="small"
        color={controls.view === "table" ? "primary" : "default"}
        onClick={() => controls.setView("table")}
      >
        <ViewList />
      </IconButton>
    </Tooltip>
    <Tooltip title="Cards">
      <IconButton
        size="small"
        color={controls.view === "cards" ? "primary" : "default"}
        onClick={() => controls.setView("cards")}
      >
        <ViewModule />
      </IconButton>
    </Tooltip>
  </Box>
);

const ShowDeletedToggle: React.VFC<{ value: boolean; onChange: (value: boolean) => void }> = ({ value, onChange }) => (
  <FormControlLabel
    className={styles.showDeleted}
    control={<Checkbox size="small" checked={value} onChange={e => onChange(e.target.checked)} />}
    label="Show deleted"
  />
);

const CataloguePagination: React.VFC<{ controls: CatalogueListControls; count: number }> = ({ controls, count }) => (
  <TablePagination
    rowsPerPageOptions={ROWS_PER_PAGE}
    component="div"
    count={count}
    rowsPerPage={controls.limit}
    page={controls.page}
    onPageChange={(_e, page) => controls.setPage(page)}
    onRowsPerPageChange={e => controls.setLimit(parseInt(e.target.value, 10))}
    SelectProps={{ MenuProps: menuProps }}
  />
);

const humanize = (value: string) => value.replaceAll("_", " ");

export const catalogueQuery = (controls: CatalogueListControls) => ({
  page: controls.page,
  limit: controls.limit,
  search: controls.debouncedSearch,
  outdated: controls.outdated,
  locale: controls.locale,
  includeDeleted: controls.showDeleted,
});

export type CatalogueQuery = ReturnType<typeof catalogueQuery>;

export const catalogueSearchParams = (params: CatalogueQuery) => {
  const qs = new URLSearchParams();
  qs.set("locale", params.locale);
  qs.set("page", String(params.page + 1));
  qs.set("limit", String(params.limit));
  if (params.search.trim()) qs.set("search", params.search.trim());
  if (params.includeDeleted) qs.set("include_deleted", "true");
  params.outdated.forEach(group => qs.append("outdated", group));
  return qs;
};

export type CatalogueItem = {
  id: number;
  name: string;
  title: string;
  image: string | null;
  icon?: React.ReactElement;
  target_joints?: string[] | null;
  updated_at?: string | null;
  deleted_at?: string | null;
  creator_id?: number | null;
  rollout?: CatalogueRollout | null;
};

export type CatalogueExtraColumn<Raw> = {
  key: string;
  label: string;
  width?: number;
  picker?: boolean;
  value: (raw: Raw) => string | string[] | null | undefined;
};

export const jointsColumn = <Raw,>(joints: (raw: Raw) => string[] | null | undefined): CatalogueExtraColumn<Raw> => ({
  key: "joints",
  label: "Joints",
  picker: true,
  value: joints,
});

export type CatalogueExtraFilter = {
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (value: string[]) => void;
};

export const jointsFilter = (controls: CatalogueListControls): CatalogueExtraFilter => ({
  label: "Joint",
  options: TARGET_JOINTS,
  value: controls.multi("joints"),
  onChange: controls.setMulti("joints"),
});

type Row<Raw> = CatalogueItem & { raw: Raw } & Record<string, unknown>;

type CatalogueListProps<Raw> = {
  kind: CatalogueKind;
  title: string;
  noun: string;
  newPath: string;
  editPath: (id: number) => string;
  controls: CatalogueListControls;
  query: {
    data?: { items: Raw[]; metadata?: { total_count?: number } };
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  toItem: (raw: Raw) => CatalogueItem;
  deletion: {
    mutate: (id: number, options: { onSuccess: () => void }) => void;
    reset: () => void;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  extraColumns?: CatalogueExtraColumn<Raw>[];
  extraFilters?: CatalogueExtraFilter[];
  localized?: boolean;
  pickerMode?: boolean;
  checkedIds?: number[];
  onPickToggle?: (raw: Raw) => void;
  onPreview?: (id: number) => void;
};

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

export const CatalogueList = <Raw,>({
  kind,
  title,
  noun,
  newPath,
  editPath,
  controls,
  query,
  toItem,
  deletion,
  extraColumns = [],
  extraFilters = [],
  localized = true,
  pickerMode = false,
  checkedIds = [],
  onPickToggle,
  onPreview,
}: CatalogueListProps<Raw>) => {
  const history = useHistory();
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const rows: Row<Raw>[] = (query.data?.items ?? []).map(raw => {
    const item = toItem(raw);
    return {
      ...Object.fromEntries(extraColumns.map(column => [column.key, column.value(raw)])),
      ...item,
      title: item.title || "Untitled",
      raw,
    };
  });
  const count = query.data?.metadata?.total_count ?? rows.length;
  const open = pickerMode
    ? undefined
    : (row: Row<Raw>) => {
        if (!row.deleted_at) history.push(editPath(row.id));
      };
  const askDelete = (id: number) => {
    deletion.reset();
    setConfirmDelete(id);
  };
  const filtered = controls.outdated.length > 0 || extraFilters.some(filter => filter.value.length > 0);
  const pagination = <CataloguePagination controls={controls} count={count} />;

  const columnHeaders = (columns: CatalogueExtraColumn<Raw>[]) =>
    Object.fromEntries(columns.map(column => [column.key, column.label]));
  const header: Record<string, string> = pickerMode
    ? {
        selected: "",
        image: "Image",
        title: "Title",
        ...columnHeaders(extraColumns.filter(column => column.picker)),
        actions: "",
      }
    : {
        id: "ID",
        image: "Image",
        title: "Title",
        ...Object.fromEntries(extraColumns.map(column => [column.key, column.label])),
        updated_at: "Updated",
        deployment: "Deployment",
        actions: "",
      };

  const widths: Record<string, number | undefined> = {
    id: 64,
    selected: 64,
    image: 72,
    title: 320,
    ...Object.fromEntries(extraColumns.map(column => [column.key, column.width])),
    updated_at: 80,
    deployment: 190,
    actions: pickerMode ? 120 : 56,
  };

  const cells = (row: Row<Raw>): Record<string, React.ReactElement> => ({
    id: <CCell>{row.id}</CCell>,
    selected: (
      <CCell>
        <Checkbox checked={checkedIds.includes(row.id)} onChange={() => onPickToggle?.(row.raw)} />
      </CCell>
    ),
    image: <ImageCell src={row.image} icon={row.icon} />,
    title: (
      <CCell>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <div className={styles.ellipsis} title={row.title}>
            {row.title}
          </div>
          {row.deleted_at && <DeletedChip />}
        </Box>
      </CCell>
    ),
    ...Object.fromEntries(extraColumns.map(column => [column.key, <ExtraCell value={row[column.key]} />])),
    updated_at: (
      <CCell>
        {row.updated_at
          ? new Date(row.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : "–"}
      </CCell>
    ),
    deployment: (
      <CCell>
        <CatalogueDeploymentRow kind={kind} name={row.name} rollout={row.rollout ?? undefined} compact />
      </CCell>
    ),
    actions: pickerMode ? (
      <CCell>
        <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={() => onPreview?.(row.id)}>
          Preview
        </Button>
      </CCell>
    ) : row.deleted_at ? (
      <CCell />
    ) : (
      <CCell>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            aria-label="Delete"
            onClick={e => {
              e.stopPropagation();
              askDelete(row.id);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </CCell>
    ),
  });

  const results = () => {
    if (query.isError) {
      return (
        <Box color="error.main" my={2}>
          Failed to load: {errorMessage(query.error)}
        </Box>
      );
    }
    if (query.isLoading && rows.length === 0) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          No {noun}s found
        </Box>
      );
    }
    if (controls.view === "cards" && !pickerMode) {
      return (
        <CardContainer>
          {rows.map(row => (
            <LibraryItem
              key={row.id}
              id={row.id}
              title={row.title}
              icon={row.deleted_at ? <DeletedChip /> : undefined}
              image={row.image ? <img alt="" src={row.image} height="100%" /> : row.icon}
              tags={row.target_joints ?? []}
              onClick={() => open?.(row)}
              onDelete={row.deleted_at ? undefined : () => askDelete(row.id)}
              deployment={<CatalogueDeploymentRow kind={kind} name={row.name} rollout={row.rollout ?? undefined} />}
            />
          ))}
        </CardContainer>
      );
    }
    return (
      <CTable<Row<Raw>, string>
        size="small"
        className={styles.table}
        items={rows}
        header={header}
        cellFactory={cells}
        columnWidths={widths}
        sortable={false}
        onRowClick={open}
      />
    );
  };

  return (
    <div>
      {!pickerMode && <CatalogueListHeader title={title} newLabel={`New ${noun}`} newPath={newPath} />}

      <Box mb={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={controls.search}
          onChange={e => controls.setSearch(e.target.value)}
          placeholder="Search by title"
          style={{ flex: 1, minWidth: 200 }}
        />
        {extraFilters.map(filter => (
          <MultiSelectFilter key={filter.label} {...filter} />
        ))}
        {!pickerMode && (
          <MultiSelectFilter
            label="Outdated on"
            options={ENV_GROUPS}
            value={controls.outdated}
            onChange={controls.setOutdated}
            optionLabel={groupLabel}
          />
        )}
        {filtered && (
          <Button
            size="small"
            onClick={() => {
              controls.setOutdated([]);
              extraFilters.forEach(filter => filter.onChange([]));
            }}
          >
            Clear
          </Button>
        )}
        {localized && <CatalogueLocaleSelect value={controls.locale} onChange={controls.setLocale} />}
        {!pickerMode && <ShowDeletedToggle value={controls.showDeleted} onChange={controls.setShowDeleted} />}
        {!pickerMode && <ViewToggle controls={controls} />}
      </Box>

      {deletion.isError && (
        <Box color="error.main" my={2}>
          {errorMessage(deletion.error)}
        </Box>
      )}

      {rows.length > 0 && pagination}
      {results()}
      {rows.length > 0 && pagination}

      <ConfirmDeleteDialog
        noun={noun}
        open={confirmDelete != null}
        loading={deletion.isLoading}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete == null) return;
          deletion.mutate(confirmDelete, { onSuccess: () => setConfirmDelete(null) });
        }}
      />
    </div>
  );
};

const DeletedChip: React.VFC = () => <Chip size="small" label="Deleted" className={styles.deleted} />;

const ExtraCell: React.VFC<{ value: unknown }> = ({ value }) => (
  <CCell>
    {Array.isArray(value) ? (
      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
        {value.map(item => (
          <Chip key={item} label={item} size="small" variant="outlined" />
        ))}
      </Box>
    ) : typeof value === "string" ? (
      humanize(value)
    ) : (
      "–"
    )}
  </CCell>
);

const ImageCell: React.VFC<{ src: string | null; icon?: React.ReactElement }> = ({ src, icon }) => (
  <CCell>
    {src ? (
      <Tooltip title={<img src={src} alt="" className={styles.preview} />} placement="right">
        <img src={src} alt="" className={styles.thumb} />
      </Tooltip>
    ) : (
      <Box className={styles.noImage}>{icon ?? "--"}</Box>
    )}
  </CCell>
);

export const ConfirmDeleteDialog: React.VFC<{
  noun: string;
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ noun, open, loading, onCancel, onConfirm }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>Delete this {noun}?</DialogTitle>
    <DialogContent>
      <DialogContentText>
        The {noun} is deleted here and on stage right away. Prod keeps serving it until you deploy the deletion from its
        prod changes page.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button
        color="secondary"
        variant="contained"
        onClick={onConfirm}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : undefined}
      >
        Delete {noun}
      </Button>
    </DialogActions>
  </Dialog>
);

const styles = {
  table: css`
    table-layout: fixed;
    width: 100%;
  `,
  thumb: css`
    width: 48px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
  `,
  preview: css`
    width: 320px;
    height: 240px;
    object-fit: cover;
    display: block;
  `,
  noImage: css`
    width: 48px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
    font-size: 12px;

    & > svg {
      width: 100%;
      height: 100%;
    }
  `,
  showDeleted: css`
    && {
      margin: 0;
      color: rgba(0, 0, 0, 0.54);
    }
    & .MuiFormControlLabel-label {
      font-size: 0.8125rem;
    }
  `,
  deleted: css`
    flex-shrink: 0;
  `,
  ellipsis: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
};
